import { checkIsNative, cn, Div, Span } from '@scatterlab/zeta-core';
import {
  ComponentProps,
  ComponentType,
  createElement,
  ElementType,
  memo,
  SVGProps,
} from 'react';

export type NavigationBarItemProps<Component extends ElementType = typeof Div> =
  {
    component?: Component;
    id: string;
    title: string;
    CustomIconComponent?: ComponentType<{
      selected: boolean;
      focused: boolean;
    }>;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    selectedIcon?: ComponentType<SVGProps<SVGSVGElement>>;
    selected?: boolean;
    /** For Native */
    focused?: boolean;
    redDot?: boolean;
  } & ComponentProps<Component>;

const NavigationBarItem = <Component extends ElementType = typeof Div>({
  component,
  id,
  title,
  CustomIconComponent,
  Icon,
  selectedIcon: FocusedIcon,
  selected,
  focused,
  className,
  redDot,
  ...props
}: NavigationBarItemProps<Component>) =>
  createElement(
    component ?? Div,
    {
      id: `navigation-item-${id}`,
      title,
      className: cn(
        'flex w-12 cursor-pointer flex-col items-center justify-center gap-1',
        !checkIsNative() && {
          'text-gray-600 focus:text-gray-300 active:text-gray-300 hover:text-gray-300':
            !selected,
          'text-white focus:text-gray-400 active:text-gray-400 hover:text-gray-400':
            selected,
        },
        className,
      ),
      tabIndex: -1,
      ...props,
    },
    (CustomIconComponent &&
      createElement(CustomIconComponent, { selected, focused })) ||
      createElement(selected ? (FocusedIcon ?? Icon) : Icon, {
        className: cn('size-6 transition-colors', {
          'text-gray-600': !selected && !focused,
          'text-gray-300': !selected && focused,
          'text-white': selected && !focused,
          'text-gray-400': selected && focused,
        }),
      }),
    redDot && (
      <Div className="absolute -top-0.5 left-[34.75px] size-[5px] rounded-full bg-error-main" />
    ),
    <Span
      className={cn('button10 transition-colors', {
        'text-gray-600': !selected && !focused,
        'text-gray-300': !selected && focused,
        'text-white': selected && !focused,
        'text-gray-400': selected && focused,
      })}
    >
      {title}
    </Span>,
  );

export default memo(NavigationBarItem) as unknown as typeof NavigationBarItem;
